import { useState } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { safeFbq } from "@/lib/consent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", firma: "", email: "", telefon: "", interesse: "Webseiten", nachricht: "" };

export default function Kontakt() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | sending | sent

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.nachricht) {
      toast.error("Bitte Name, E-Mail und Nachricht ausfüllen.");
      return;
    }
    try {
      setStatus("sending");
      const res = await axios.post(`${API}/contact`, form);
      if (res.status >= 200 && res.status < 300) {
        setStatus("sent");
        // Meta Pixel — Lead-Event (nur mit Marketing-Consent)
        safeFbq("track", "Lead", {
          content_name: "Kontaktformular",
          content_category: form.interesse || "Anfrage",
        });
        toast.success("Anfrage gesendet. Wir melden uns innerhalb 48 Stunden.");
        setForm(initial);
      } else {
        throw new Error("Fehler");
      }
    } catch (err) {
      setStatus("idle");
      toast.error("Es ist ein Fehler aufgetreten. Bitte später erneut versuchen.");
    }
  };

  return (
    <section id="kontakt" ref={ref} className="relative bg-[#0A0A0A] text-[#FAFAF7] overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 py-24 md:py-40 grid grid-cols-12 gap-6 lg:gap-16">
        <div className="col-span-12 lg:col-span-5">
          <div className="flex items-center gap-4 mb-8">
            <span className="chapter-num text-[#C9A961]">05 — Kontakt</span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif-editorial text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
          >
            Kostenloses<br /><em className="italic text-[#C9A961]">Erstgespräch</em>.
          </motion.h2>
          <p className="mt-6 text-[#FAFAF7]/70 max-w-md leading-relaxed">
            30 Minuten. Unverbindlich. Klare Antworten. Wir melden uns innerhalb von 48 Stunden mit einem Terminvorschlag.
          </p>

          <div className="mt-14 space-y-6">
            <div className="border-t border-white/10 pt-5">
              <div className="font-mono-eyebrow text-[#FAFAF7]/50">Adresse</div>
              <div className="font-serif-editorial text-xl mt-1">Sonnrainstrasse 13, 3510 Konolfingen</div>
            </div>
            <div className="border-t border-white/10 pt-5">
              <div className="font-mono-eyebrow text-[#FAFAF7]/50">E-Mail</div>
              <div className="font-serif-editorial text-xl mt-1">mhudak@hudakenterprises.ch</div>
            </div>
            <div className="border-t border-white/10 pt-5">
              <div className="font-mono-eyebrow text-[#FAFAF7]/50">Telefon</div>
              <div className="font-serif-editorial text-xl mt-1">+41 77 435 15 61</div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          noValidate
          className="col-span-12 lg:col-span-7 lg:pl-16 border-t lg:border-t-0 lg:border-l border-white/10 pt-10 lg:pt-0"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
            <div>
              <label className="he-input-label">Name *</label>
              <input value={form.name} onChange={upd("name")} type="text" required className="he-input dark" placeholder="Vor- und Nachname" data-testid="contact-name" />
            </div>
            <div>
              <label className="he-input-label">Firma</label>
              <input value={form.firma} onChange={upd("firma")} type="text" className="he-input dark" placeholder="Unternehmen" data-testid="contact-firma" />
            </div>
            <div>
              <label className="he-input-label">E-Mail *</label>
              <input value={form.email} onChange={upd("email")} type="email" required className="he-input dark" placeholder="name@firma.ch" data-testid="contact-email" />
            </div>
            <div>
              <label className="he-input-label">Telefon</label>
              <input value={form.telefon} onChange={upd("telefon")} type="tel" className="he-input dark" placeholder="+41 …" data-testid="contact-telefon" />
            </div>
            <div className="md:col-span-2">
              <label className="he-input-label">Interesse</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Webseite", "Meta Ads", "Beides", "Beratung"].map((opt) => (
                  <button
                    type="button"
                    key={opt}
                    onClick={() => setForm((f) => ({ ...f, interesse: opt }))}
                    className={`px-4 py-2 rounded-full border text-sm transition-colors duration-300 ${
                      form.interesse === opt
                        ? "border-[#C9A961] bg-[#C9A961]/10 text-[#C9A961]"
                        : "border-white/15 text-[#FAFAF7]/70 hover:border-white/40"
                    }`}
                    data-testid={`interesse-${opt}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="he-input-label">Nachricht *</label>
              <textarea value={form.nachricht} onChange={upd("nachricht")} rows={4} required className="he-input dark resize-none" placeholder="Kurz zu Ihrem Vorhaben, Zielen, Zeitrahmen …" data-testid="contact-nachricht" />
            </div>
          </div>

          <div className="mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <p className="text-[13px] text-[#FAFAF7]/50 max-w-md">
              Mit dem Absenden erklären Sie sich mit der Verarbeitung Ihrer Daten gemäss unserer Datenschutzerklärung einverstanden.
            </p>
            <button
              type="submit"
              disabled={status === "sending"}
              className="btn-gold disabled:opacity-70"
              data-testid="contact-submit"
            >
              {status === "sending" && <><Loader2 size={16} className="animate-spin" /> Wird gesendet …</>}
              {status === "sent" && <><Check size={16} /> Gesendet</>}
              {status === "idle" && <>Anfrage senden <ArrowUpRight size={16} /></>}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
